import {Component, ConnectedComponent, StoreProvider} from 'lowkey-component'
import Swiper, {Mousewheel, Navigation} from 'swiper'

import {MediaQuery} from '@services/MediaQuery'
import {ScrollService} from '@services/ScrollService'
import {createTrainingState} from '@services/TrainingState'
import {WindowService} from '@services/WindowService'
import {appSelectors} from '@store/appSlice'
import {trainingSelectors} from '@store/trainingSlice'

type Ref = {
  swiper: Component<HTMLDivElement> | undefined;
  next: Component<HTMLDivElement> | undefined;
  prev: Component<HTMLDivElement> | undefined;
  close: Component<HTMLDivElement> | undefined;
};

export class TrainingsDialog extends ConnectedComponent<HTMLDivElement, Ref> {
  private swiper: Swiper | null = null;
  private mq = MediaQuery.create();
  private slides: HTMLDivElement[] = [];
  private trainingState = createTrainingState();
  private scroll = ScrollService.create();
  private windowService = WindowService.create();
  private currentModalId: string | undefined = undefined;

  constructor(element: HTMLDivElement) {
    super(element);
    this.refs = {
      swiper: undefined,
      next: undefined,
      prev: undefined,
      close: undefined,
    };
  }

  renderNext() {
    const nextNav = this.refs.next?.element as HTMLDivElement;
    const nextNavTitle = nextNav.querySelector(
      '.m-training-navigation-item_title',
    ) as HTMLImageElement;
    const nextNavBody = nextNav.querySelector(
      '.m-training-navigation-item_body',
    ) as HTMLImageElement;

    const {realIndex = 0} = this.swiper || {};
    realIndex - 1 < 0 ? this.slides.length - 1 : realIndex - 1;
    const nextIndex =
      realIndex + 1 > this.slides.length - 1 ? 0 : realIndex + 1;

    const nextTitle = this.slides[nextIndex].querySelector(
      '.o-training-dialog-item_title > p',
    ) as HTMLSpanElement;

    const nextBody = this.slides[nextIndex].querySelector(
      '.o-training-dialog-item_heading > p',
    ) as HTMLSpanElement;

    nextNavTitle.textContent = nextTitle.textContent;
    nextNavBody.textContent = nextBody.textContent;
  }

  renderPrev() {
    const prevNav = this.refs.prev?.element as HTMLDivElement;
    // const prevNav = this.element.querySelector(
    //   '.o-trainings-dialog_left_card',
    // ) as HTMLImageElement;
    const prevNavTitle = prevNav.querySelector(
      '.m-training-navigation-item_title',
    ) as HTMLImageElement;
    const prevNavBody = prevNav.querySelector(
      '.m-training-navigation-item_body',
    ) as HTMLImageElement;

    const {realIndex = 0} = this.swiper || {};
    // console.log(this.swiper.realIndex)
    // const prevIndex =
    realIndex - 1 < 0 ? this.slides.length - 1 : realIndex - 1;
    realIndex + 1 > this.slides.length - 1 ? 0 : realIndex + 1;
    const prevIndex =
      realIndex - 1 < 0 ? this.slides.length - 1 : realIndex - 1;

    const prevTitle = this.slides[prevIndex].querySelector(
      '.o-training-dialog-item_title > p',
    ) as HTMLSpanElement;

    const prevBody = this.slides[prevIndex].querySelector(
      '.o-training-dialog-item_heading > p',
    ) as HTMLSpanElement;

    prevNavTitle.textContent = prevTitle.textContent;
    prevNavBody.textContent = prevBody.textContent;
  }

  render() {
    this.renderPrev();
    this.renderNext();
  }

  getIndexByPostId(modalId: string) {
    return this.slides.findIndex((slide) => {
      const id = slide.dataset['id'];
      return id === modalId;
    });
  }

  didMount() {
    if (!this.refs.swiper?.element) {
      return;
    }
    this.windowService.onResize.connect(this.onResizeWindow);
    this.observe(appSelectors.selectOrientation, this.onChangeOrientation);

    this.slides = Array.from(this.element.querySelectorAll('.swiper-slide'));

    // this.observe(trainingSelectors.selectIsOpenTrainingModal, (isOpen) => {
    //   !isOpen && this.hide();
    // });

    this.observe(trainingSelectors.selectCurrentId, (id) => {
      if (id === undefined) {
        this.hide();
      } else {
        const index = this.getIndexByPostId(id);
        const modal = trainingSelectors.selectTrainingModalId(id)(
          StoreProvider.create().getState(),
        );
        if (modal && !modal.isOpen) {
          this.currentModalId = id;
          this.show(index);
        }
      }
    });

    this.swiper = new Swiper(this.refs.swiper.element, {
      modules: [Navigation, Mousewheel],
      slidesPerView: 'auto',
      direction: 'horizontal',
      centeredSlides: true,
      allowTouchMove: true,
      spaceBetween: this.mq.isDesktop() ? 0 : 27,
      preventInteractionOnTransition: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: this.refs.next?.element,
        prevEl: this.refs.prev?.element,
      },
      on: {
        slideChange: () => {
          this.render();
        },
        init: () => {
          this.render();
        },
      },
    });

    this.refs.close?.on('click', () => {
      this.hide();
    });

    this.resize();
  }

  onChangeOrientation = () => {
    if (this.mq.isPhone()) {
      this.resize();
    }
  };

  resize = () => {
    const height = window.innerHeight;
    this.element.style.height = `${height}px`;
    this.slides.map((slide) => {
      const item = slide.querySelector<HTMLDivElement>(
        '.o-training-dialog-item',
      );
      if (item) {
        item.style.height = `${height}px`;
      }
    });
  };

  onResizeWindow = () => {
    if (this.mq.isDesktop()) {
      this.resize();
    }
  };

  didUnmount() {
    this.windowService.onResize.disconnect(this.onResizeWindow);
    this.swiper?.destroy();
  }

  show(index: number) {
    if (index <= -1) {
      return;
    }
    const top = this.scroll.stopScroll(document.body);
    this.element.style.top = `${top}px`;
    this.swiper?.slideTo(index, 0);
    this.resize();
    this.element.classList.add('show');
    this.currentModalId &&
      this.trainingState.appendTrainingModal({
        id: this.currentModalId,
        isOpen: true,
      });
  }

  hide() {
    this.scroll.startScroll();
    this.element.style.top = `${0}px`;
    this.element.classList.remove('show');
    this.currentModalId &&
      this.trainingState.removeTrainingModal(this.currentModalId);
  }
}
